import { Module } from '@/modules/companies'
import { ALL_ABSENCE_TYPES_QUERY } from '@/modules/timesheets/queries'
import type { AbsenceTypeNode } from '@/modules/timesheets/types.graphql'
import { useConfirm } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { BrowserView, isMobileOnly, MobileOnlyView } from 'react-device-detect'
import {
  AbsenceImport,
  AbsenceManagement,
  AbsenceSettingsCards,
  AbsenceSettingsTable,
  AbsenceTypeDialog,
} from '../components'
import type { AbsenceTypeForm } from '../components/AbsenceSettings/AbsenceTypePrompt'
import { AbsenceTypePrompt } from '../components/AbsenceSettings/AbsenceTypePrompt'
import { InfoBox, Section, SettingsWrapper } from '../components/common'
import { ModuleResponsibleUsers } from '../components/ModuleResponsibleUsers'
import { useAbsenceSettingsMutations } from '../util/mutations.hooks'

export const LS_KEY_LIST_VIEW_MODE_ABSENCE_SETTINGS =
  'firmadok-list-view-mode-absence-type-settings'

interface AbsenceSettingsProps {}

export const AbsenceSettings: React.FC<AbsenceSettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.absence-types-1',
    info2: 'settings.info.absence-types-2',

    absence: 'common.absence',
    absenceTypes: 'timesheets.absence-types',
    addAbsenceType: 'settings.add-absence-type',
    editAbsenceType: 'settings.edit-absence-type',
    add: 'common.add',
    manage: 'settings.absence.manage-absences',

    validation: {
      nameExists: 'settings.validation.absence-type-name-exists',
    },
    prompt: {
      add: 'settings.prompts.absence-type-create',
      delete: ['settings.prompts.absence-type-delete', { name: '' }],
      deleteTitle: 'settings.prompts.absence-type-delete-title',
    },
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()

  const [temporaryEditColor, setTemporaryEditColor] = useState<{
    [id: string]: string | null
  }>({})

  const { data: queryData, loading: queryLoading } = useQuery(
    ALL_ABSENCE_TYPES_QUERY,
    {
      onError: console.error,
    }
  )

  const mutations = useAbsenceSettingsMutations()

  function handleUpdateColor(id: string) {
    return async (color: string) => {
      setTemporaryEditColor(v => ({
        ...v,
        [id]: color,
      }))

      await mutations
        .patch({
          variables: { id, input: { color } },
        })
        .finally(() => {
          setTemporaryEditColor(v => ({
            ...v,
            [id]: null,
          }))
        })
    }
  }

  const absenceTypes = useMemo(
    () => queryData?.allAbsenceTypes ?? [],
    [queryData?.allAbsenceTypes]
  )

  async function handleAddAbsenceType() {
    const { data } = await addPrompt<AbsenceTypeForm | null>(resolve => (
      <AbsenceTypePrompt
        existingAbsenceTypes={absenceTypes}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.create({
      variables: {
        input: {
          externalId: data.externalId,
          name: data.name,
          color: data.color,
          defaultDayValue: data.defaultDayValue,
        },
      },
    })
  }

  const handleEditAbsenceType = useCallback(
    async (type: AbsenceTypeNode) => {
      const { data } = await addPrompt<AbsenceTypeForm | null>(resolve => (
        <AbsenceTypePrompt
          initialData={type}
          existingAbsenceTypes={absenceTypes}
          onSubmit={resolve}
        />
      ))
      if (data === null) return

      void mutations.patch({
        variables: {
          id: type.id,
          input: {
            externalId: data.externalId,
            name: data.name,
            color: data.color,
            defaultDayValue: data.defaultDayValue,
          },
        },
      })
    },
    [absenceTypes, addPrompt, mutations]
  )

  const handleDeleteAbsenceType = useCallback(
    async (type: AbsenceTypeNode) => {
      const { data: answer } = await confirm(
        translations.prompt.delete({ name: type.name }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      void mutations.delete({
        variables: {
          id: type.id,
        },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const isLoading = queryLoading || mutations.loading

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>

      <ModuleResponsibleUsers module={Module.ABSENCES} />

      <Section
        header={translations.absenceTypes}
        loading={isLoading}
        actions={
          <BrowserView>
            <AbsenceTypeDialog />
          </BrowserView>
        }
      >
        <MobileOnlyView>
          <AbsenceSettingsCards
            absenceTypes={absenceTypes}
            onAddSubmit={handleAddAbsenceType}
            onEditSubmit={handleEditAbsenceType}
            onDelete={handleDeleteAbsenceType}
          />
        </MobileOnlyView>

        <BrowserView>
          <AbsenceSettingsTable
            absenceTypes={absenceTypes}
            temporaryEditColor={temporaryEditColor}
            onAddSubmit={handleAddAbsenceType}
            onEditSubmit={handleEditAbsenceType}
            onDelete={handleDeleteAbsenceType}
            onUpdateColor={handleUpdateColor}
          />
        </BrowserView>
      </Section>

      <AbsenceManagement domain="settings" />

      <AbsenceImport />
    </SettingsWrapper>
  )
}
